import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import AmateurOnboardingChecklist from "@/components/AmateurOnboardingChecklist";
import { GraduationCap, Trophy, Award, ChevronRight, Sparkles, CheckCircle2, Star } from "lucide-react";

const LEVEL_ORDER = ["explorer", "specialist", "expert"];

export default function AcademyHome() {
  const { user } = useAuth();
  const { lang, t } = useT();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    setBusy(true);
    api.get(`/academy/courses?lang=${lang}`)
      .then((r) => setData(r.data))
      .finally(() => setBusy(false));
  }, [lang]);

  const grouped = {};
  (data?.courses || []).forEach((c) => {
    (grouped[c.level] = grouped[c.level] || []).push(c);
  });
  const totalCourses = (data?.courses || []).length;

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-10 md:py-16" data-testid="academy-home">
      <div className="flex items-start justify-between flex-wrap gap-6">
        <div>
          <span className="chip-pink inline-flex items-center gap-1.5"><GraduationCap className="w-3 h-3" /> {t("academy.hero.chip")}</span>
          <h1 className="font-display text-4xl md:text-6xl text-[#1F3A5F] mt-4 leading-[1.02] font-medium">
            {t("academy.hero.title1")}<span className="text-[#E5276A]">{t("academy.hero.title2")}</span>{t("academy.hero.title3")}
          </h1>
          <p className="text-[#4A5568] mt-4 max-w-2xl text-base md:text-lg font-light">
            {t("academy.hero.sub")}
          </p>
        </div>

        {user && data?.user_stats && (
          <div className="app-card p-5 min-w-[220px]" data-testid="academy-stats">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold">{t("academy.passport.title")}</div>
            <div className="mt-3 grid grid-cols-3 gap-4">
              <StatCell icon={<Star className="w-4 h-4 text-amber-500" />} label={t("academy.stat.xp")} value={data.user_stats.xp} />
              <StatCell icon={<Trophy className="w-4 h-4 text-[#E5276A]" />} label={t("academy.stat.done")} value={data.user_stats.completed} />
              <StatCell icon={<Award className="w-4 h-4 text-[#1F3A5F]" />} label={t("academy.stat.badges")} value={(data.user_stats.badges || []).length} />
            </div>
          </div>
        )}
      </div>

      {busy && <div className="mt-16 text-center text-[#8B95A1]">{t("academy.loading")}</div>}

      {!busy && (
        <div className="mt-10">
          <AmateurOnboardingChecklist />
        </div>
      )}

      {!busy && totalCourses === 0 && (
        <div className="mt-16 app-card p-10 text-center max-w-xl mx-auto" data-testid="academy-empty">
          <GraduationCap className="w-10 h-10 text-[#E5276A] mx-auto opacity-70" />
          <h2 className="font-display text-2xl text-[#1F3A5F] mt-4">{t("academy.empty.title")}</h2>
          <p className="text-sm text-[#4A5568] mt-2">{t("academy.empty.body")}</p>
        </div>
      )}

      {!busy && LEVEL_ORDER.map((level) => {
        const list = grouped[level] || [];
        if (!list.length) return null;
        return (
          <section key={level} className="mt-14" data-testid={`academy-section-${level}`}>
            <div className="flex items-center gap-3 mb-6">
              <h2 className="font-display text-3xl md:text-4xl text-[#1F3A5F]">{t(`academy.level.${level}`)}</h2>
              <span className="text-[10px] uppercase tracking-widest text-[#8B95A1]">{t("academy.section.count").replace("{n}", list.length)}</span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {list.map((c) => <CourseCard key={c.slug} c={c} loggedIn={!!user} t={t} />)}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function StatCell({ icon, label, value }) {
  return (
    <div className="text-center">
      <div className="flex justify-center mb-0.5">{icon}</div>
      <div className="font-display text-2xl text-[#1F3A5F] leading-none">{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-[#8B95A1] mt-0.5">{label}</div>
    </div>
  );
}

function CourseCard({ c, loggedIn, t }) {
  const p = c.progress || {};
  const isCertified = p.certified;
  const isStarted = p.started;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const coverSrc = c.cover_url ? `${backendUrl}${c.cover_url}` : null;
  return (
    <Link
      to={`/courses/${c.slug}`}
      data-testid={`academy-course-${c.slug}`}
      className="app-card overflow-hidden hover:border-[#E5276A] transition-all group block relative"
    >
      <div
        className="h-40 flex items-center justify-center relative overflow-hidden"
        style={{
          backgroundColor: c.color_hex + "44",
          backgroundImage: coverSrc
            ? `url(${coverSrc})`
            : `linear-gradient(135deg, ${c.color_hex}22, ${c.color_hex}66)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {!coverSrc && <span className="text-6xl">{c.emoji}</span>}
        {coverSrc && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        )}
        {coverSrc && (
          <div className="absolute bottom-2 left-3 text-3xl drop-shadow-lg" aria-hidden>{c.emoji}</div>
        )}
        {isCertified && (
          <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center" title={t("academy.card.viewCert")}>
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          </div>
        )}
        {isStarted && !isCertified && (
          <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-white/95 text-[9px] font-bold text-[#E5276A] tracking-widest uppercase">
            {t("academy.card.inProgress")}
          </div>
        )}
        {c.translation_fallback && (
          <div
            className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[9px] font-bold tracking-widest uppercase"
            title={t("course.fallback.title")}
          >
            FR
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#8B95A1] font-bold">
          <span>{c.level_label}</span>
          <span>·</span>
          <span>{c.region}</span>
          <span className="ml-auto">{c.duration_min}min</span>
        </div>
        <h3 className="font-display text-xl text-[#1F3A5F] mt-2 leading-tight">{c.title}</h3>
        <p className="text-sm text-[#4A5568] mt-1.5 leading-relaxed">{c.subtitle}</p>
        <div className="mt-4 flex items-center justify-between">
          <div className="inline-flex items-center gap-1 text-xs font-bold text-[#E5276A]">
            <Sparkles className="w-3 h-3" /> +{c.xp_reward} XP
          </div>
          <span className="inline-flex items-center gap-1 text-xs font-bold text-[#1F3A5F] group-hover:text-[#E5276A]">
            {isCertified ? t("academy.card.viewCert") : isStarted ? t("academy.card.continue") : loggedIn ? t("academy.card.start") : t("academy.card.discover")}
            <ChevronRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}
